import { useCallback, useEffect, useRef } from "react";

import { EventType, trackEvent } from "@/shared/utils/core";
import { secondsSince } from "@/shared/utils/funnelAnalytics";

import type { PromptLocation } from "@/shared/utils/funnelAnalytics";
import type { PaymentTier } from "@/shared/utils/tierOrder";

interface UsePricingViewTrackingOptions {
  isOpen: boolean;
  promptLocation: PromptLocation;
  currentTier?: PaymentTier | null;
  estateId?: number | null;
}

export function usePricingViewTracking({
  isOpen,
  promptLocation,
  currentTier,
  estateId,
}: UsePricingViewTrackingOptions) {
  const viewedAt = useRef(0);
  const propertiesRef = useRef({ promptLocation, currentTier, estateId });
  propertiesRef.current = { promptLocation, currentTier, estateId };

  useEffect(() => {
    if (isOpen) {
      const properties = propertiesRef.current;
      viewedAt.current = Date.now();
      trackEvent(EventType.PRICING_VIEWED, {
        prompt_location: properties.promptLocation,
        current_tier: properties.currentTier ?? null,
        estate_id: properties.estateId != null ? String(properties.estateId) : null,
      });
    }
  }, [isOpen]);

  const trackDismissed = useCallback(() => {
    if (viewedAt.current) {
      trackEvent(EventType.PAYWALL_DISMISSED, {
        prompt_location: propertiesRef.current.promptLocation,
        time_on_paywall_seconds: secondsSince(viewedAt.current),
      });
      viewedAt.current = 0;
    }
  }, []);

  return { trackDismissed };
}
